import React, { useEffect, useState } from "react";
import { generateAPIKey } from "../../../utils/helper";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import { FaCopy } from "react-icons/fa6";
import { PublishData } from "../../../controllers/APIController";

/**
 * ApiModal Component
 * Modal for creating new API keys
 * Generates secure API keys and allows users to copy them
 * @param {Object} props - Component props
 * @param {Function} props.onSubmitHandler - Function to call after successful submission
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onOpen - Function to open the modal
 * @param {Function} props.onOpenChange - Function to handle modal open state changes
 * @returns {JSX.Element} API key creation modal
 */
const ApiModal = ({ onSubmitHandler, isOpen, onOpen, onOpenChange }) => {
  // Component state
  const [keyName, setKeyName] = useState("");
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [fieldError, setFieldError] = useState(null);
  
  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const userId = userLogged && userLogged.user.id;
  const orgInfo = userLogged && userLogged.user.LastOrgId;

  /**
   * Copy API key to clipboard
   * Uses navigator.clipboard API to copy the generated key
   */
  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(apiKeyValue)
      .then(() => {
        // Successfully copied
      })
      .catch((error) => {
        console.error("Failed to copy text:", error);
      });
  };

  /**
   * Submit API key creation
   * Validates input and sends data to API
   * @param {Function} onClose - Function to close the modal
   */
  const handleSubmit = async (onClose) => {
    if (keyName === "") {
      setFieldError("Name field is required!");
      return;
    }

    const payloadData = {
      api_key: apiKeyValue,
      Organization: [orgInfo],
      users_permissions_user: [userId],
      name: keyName,
    };
    
    try {
      await PublishData({ data: payloadData }, "keys");
      onSubmitHandler();
      onClose();
    } catch (error) {
      console.error("Error creating API key:", error);
    }
  };

  /**
   * Handle keyboard events for form submission
   * Allows submission on Enter key press
   * @param {KeyboardEvent} event - Keyboard event object
   * @param {Function} onClose - Function to close the modal
   */
  const handleKeyDown = (event, onClose) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent default form submission
      handleSubmit(onClose);
    }
  };

  /**
   * Generate new API key on component mount
   * Creates a secure API key when modal opens
   */
  useEffect(() => {
    setApiKeyValue(generateAPIKey());
  }, []);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        {(onClose) => (
          <div
            onKeyDown={(event) => handleKeyDown(event, onClose)}
            tabIndex="0"
            className="outline-none"
          >
            {/* Modal Header */}
            <ModalHeader className="flex flex-col gap-1">
              Generate API Key
            </ModalHeader>
            
            {/* Modal Body */}
            <ModalBody>
              <div>
                {/* API Key Name Input */}
                <Input
                  type="text"
                  className="mb-12"
                  isRequired
                  label="Name"
                  placeholder=" "
                  variant="faded"
                  labelPlacement="outside"
                  value={keyName || ""}
                  onChange={(event) => {
                    setKeyName(event.target.value);
                  }}
                  isInvalid={fieldError}
                  errorMessage={fieldError}
                />
                
                {/* Generated API Key Display */}
                <Input
                  type="text"
                  className="mb-8 mt-4"
                  isRequired
                  label="API Key"
                  endContent={
                    <FaCopy
                      onClick={handleCopyClick}
                      className="cursor-pointer"
                    />
                  }
                  placeholder=" "
                  variant="faded"
                  labelPlacement="outside"
                  value={apiKeyValue || ""}
                  readOnly
                />
              </div>
            </ModalBody>
            
            {/* Modal Footer */}
            <ModalFooter>
              {/* Cancel Button */}
              <Button 
                variant="ghost" 
                className="text-slate-600 hover:text-slate-900 px-4" 
                onPress={onClose}
              >
                Cancel
              </Button>
              
              {/* Add Button */}
              <Button
                color="primary"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
                onClick={() => handleSubmit(onClose)}
                onKeyDown={(event) => handleKeyDown(event, onClose)}
              >
                Add
              </Button>
            </ModalFooter>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ApiModal;
